// VOY-461 — RefinementBar. The "Tell Voy what to change" NL input, the TRY
// suggestion chips, and the "VOY IS FILTERING FOR" summary (design: .refine,
// .refine-egs, .fsum/.fpill).
//
// THE SYNC: the fpills are rendered from the SAME RefinementConstraintSet the
// NL bar edits. Removing a pill calls removeConstraint and hands the new set
// up via onConstraintChange — exactly what an NL "undo" produces. There is no
// separate pill state. (Design class names are namespaced vmt-* to avoid
// colliding with global app CSS — flagged adaptation.)

import { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  resolveEffectiveFraming,
  removeConstraint,
  type RefinementConstraintSet,
  type ConstraintType,
} from '@/lib/search/refinement-constraints'

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
  </svg>
)
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
)
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const TRY_SUGGESTIONS = ['Miami not Honolulu', 'cheaper hotel', '+2 nights', 'only American']

/** A display pill derived from the constraint set, with how to remove it. */
interface DisplayPill {
  key: string
  label: string
  active?: boolean
  changed?: boolean
  /** Removing this pill → which constraint to drop. Absent = not removable. */
  remove?: { type: ConstraintType; priceComponent?: 'flights' | 'hotels' | 'trip' }
}

function summaryPills(
  set: RefinementConstraintSet,
  changedTypes: Set<ConstraintType>,
): DisplayPill[] {
  const eff = resolveEffectiveFraming(set)
  const pills: DisplayPill[] = []

  // Destination — always shown; active styling. "Honolulu → Maui" when overridden.
  if (eff.destination) {
    const overridden = set.constraints.some((c) => c.type === 'destination_override')
    const baseDest = set.base.destination
    const label = overridden && baseDest && baseDest !== eff.destination
      ? `${baseDest} → ${eff.destination}`
      : eff.destination
    pills.push({
      key: 'destination',
      label,
      active: true,
      changed: changedTypes.has('destination_override'),
      remove: overridden ? { type: 'destination_override' } : undefined,
    })
  }

  // Airline — "Any airline" when unconstrained.
  const air = eff.filters.airline
  pills.push(
    air
      ? {
          key: 'airline',
          label: air.mode === 'only' ? air.carriers.join(' + ') : `Excluding ${air.carriers.join(', ')}`,
          changed: changedTypes.has('airline'),
          remove: { type: 'airline' },
        }
      : { key: 'airline', label: 'Any airline' },
  )

  // Hotel brand — only shown when constrained (NL "Marriott only" / "no chains").
  const brand = eff.filters.hotelBrand
  if (brand?.brands?.length) {
    pills.push({
      key: 'hotel_brand',
      label: brand.mode === 'only' ? brand.brands.join(' + ') : `Excluding ${brand.brands.join(', ')}`,
      changed: changedTypes.has('hotel_brand'),
      remove: { type: 'hotel_brand' },
    })
  }

  // Price ceiling (flights/hotels/trip).
  for (const scope of ['flights', 'hotels', 'trip'] as const) {
    const amt = eff.filters.priceCeilings[scope]
    if (typeof amt === 'number') {
      pills.push({
        key: `price-${scope}`,
        label: `Under $${amt}${scope !== 'trip' ? ` ${scope}` : ''}`,
        changed: changedTypes.has('price_ceiling'),
        remove: { type: 'price_ceiling', priceComponent: scope },
      })
    }
  }

  // Stars.
  if (eff.filters.stars?.length) {
    pills.push({
      key: 'stars',
      label: `${eff.filters.stars.join('/')}★`,
      changed: changedTypes.has('star'),
      remove: { type: 'star' },
    })
  }

  // Nonstop.
  if (eff.filters.nonstopOnly) {
    pills.push({ key: 'nonstop', label: 'Nonstop only', changed: changedTypes.has('nonstop_only'), remove: { type: 'nonstop_only' } })
  }

  // Cabin (when overridden from base).
  if (eff.cabin && set.constraints.some((c) => c.type === 'cabin')) {
    pills.push({ key: 'cabin', label: cabinLabel(eff.cabin), changed: changedTypes.has('cabin'), remove: { type: 'cabin' } })
  }

  return pills
}

function cabinLabel(c: string) {
  return c.replace('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

export interface RefinementBarProps {
  constraintSet: RefinementConstraintSet
  /** A refinement was requested in natural language. */
  onSubmitUtterance: (utterance: string) => void
  /** The set changed via a pill (remove). Parent re-runs the refine. */
  onConstraintChange: (next: RefinementConstraintSet) => void
  /** Constraint types changed by the most recent refine (highlight as "changed"). */
  changedTypes?: ConstraintType[]
  /** When set, render the typed text statically (mid-refinement design state). */
  typedText?: string
  /** A short message from the last refine (e.g. "couldn't parse — rephrase"). */
  notice?: string
  /** Running list of applied refinements (newest last). */
  changeLog?: Array<{ id: number; text: string; recovered?: boolean }>
  disabled?: boolean
}

export function RefinementBar({
  constraintSet,
  onSubmitUtterance,
  onConstraintChange,
  changedTypes,
  typedText,
  notice,
  changeLog,
  disabled,
}: RefinementBarProps) {
  const [value, setValue] = useState('')
  const changed = new Set<ConstraintType>(changedTypes ?? [])
  const pills = summaryPills(constraintSet, changed)

  const submit = (text: string) => {
    const t = text.trim()
    if (!t || disabled) return
    onSubmitUtterance(t)
    setValue('')
  }

  return (
    <div className="vmt-refine-wrap">
      <div className="vmt-refine">
        <span className="vmt-refine__ic"><SparkIcon /></span>
        {typedText != null ? (
          <div className="vmt-refine__typed">
            {typedText}
            <span className="vmt-refine__cursor" aria-hidden="true" />
          </div>
        ) : (
          <input
            className="vmt-refine__input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(value) }}
            placeholder="Tell Voy what to change — 'cheaper hotel', 'different dates', 'only American'…"
            disabled={disabled}
            aria-label="Tell Voy what to change"
          />
        )}
        <button type="button" className="vmt-refine__send" onClick={() => submit(value)} disabled={disabled} aria-label="Apply refinement">
          <SendIcon />
        </button>
      </div>

      {notice ? <div className="vmt-refine__notice" role="status">{notice}</div> : null}

      {changeLog && changeLog.length > 0 ? (
        <div className="vmt-changelog" aria-label="Changes you've made">
          <span className="vmt-changelog__lead">Changes you've made</span>
          <ul className="vmt-changelog__list">
            {changeLog.slice(-6).map((c) => (
              <li key={c.id} className="vmt-changelog__item">
                <CheckIcon />
                <span>{c.text.replace(/^got it[\s,.—–-]*/i, '') || c.text}</span>
                {c.recovered ? <span className="vmt-changelog__tag">reassembled</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="vmt-refine-egs">
        <span className="vmt-refine-egs__lbl">TRY</span>
        {TRY_SUGGESTIONS.map((s) => (
          <button key={s} type="button" className="vmt-eg" onClick={() => submit(s)} disabled={disabled}>
            {s}
          </button>
        ))}
      </div>

      <div className="vmt-fsum">
        <span className="vmt-fsum__lead">Voy is filtering for</span>
        {pills.map((p) => (
          <span
            key={p.key}
            className={cn('vmt-fpill', p.active && 'vmt-fpill--act', p.changed && 'vmt-fpill--changed')}
          >
            {p.label}
            {p.remove && (
              <button
                type="button"
                className="vmt-fpill__x"
                aria-label={`Remove ${p.label}`}
                onClick={() => onConstraintChange(removeConstraint(constraintSet, p.remove!.type, p.remove!.priceComponent ? { priceComponent: p.remove!.priceComponent } : undefined))}
              >
                <XIcon />
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}
